import type { TFunction } from "i18next";

import {
  OrderingCheckListChild,
  OrderingUmbrellaSelectOption,
} from "../../../components/ordering-check-card/types";
import { UserDetails } from "../../../types";
import { isAddressBasedCheck } from "../../../utils/isAddressBasedCheck";
import { getTotalAddOnsCost } from "../../order/order-utils";
import { CheckData } from "../types";
import {
  Package,
  SelectedBundlesType,
  SelectedChecksType,
} from "./PackageContext";

export function isCheckStandaloneDisabled(
  orderChecks: any,
  check: Pick<SelectedChecksType, "must_be_ordered_with_one_of">
) {
  if (
    check.must_be_ordered_with_one_of === undefined ||
    check.must_be_ordered_with_one_of.length === 0
  )
    return false;

  return !orderChecks.some((orderCheck: { key: string }) =>
    check.must_be_ordered_with_one_of?.includes(orderCheck.key)
  );
}

function getBundleCheckTypes(bundles: SelectedBundlesType[]): Set<string> {
  const types = new Set<string>();

  bundles.forEach((bundle) => {
    if (bundle.checks?.length) {
      bundle.checks.forEach((check) => {
        types.add(check.type);
      });
      return;
    }

    Object.keys(bundle.check_types_with_arguments || {}).forEach(
      (checkType) => {
        types.add(checkType);
      }
    );
  });

  return types;
}

function getCheckTypesWithArguments(
  bundles: SelectedBundlesType[],
  checks: SelectedChecksType[]
) {
  const bundleCheckTypes = getBundleCheckTypes(bundles);
  const seen = new Set<string>();
  const result: { check_type: string; arguments: Record<string, unknown> }[] =
    [];

  checks.forEach((check) => {
    if (bundleCheckTypes.has(check.key) || seen.has(check.key)) {
      return;
    }
    seen.add(check.key);
    result.push({
      check_type: check.key,
      arguments:
        typeof check.check_arguments === "object" &&
        check.check_arguments !== null &&
        !Array.isArray(check.check_arguments)
          ? (check.check_arguments as Record<string, unknown>)
          : {},
    });
  });

  return result;
}

function getCheckTypesWithArgumentsIncludingBundles(
  bundles: SelectedBundlesType[],
  checks: SelectedChecksType[]
) {
  const seen = new Set<string>();
  const result: { check_type: string; arguments: Record<string, unknown> }[] =
    [];

  const pushCheck = (
    checkType: string,
    args: Record<string, unknown> | unknown
  ) => {
    if (seen.has(checkType)) {
      return;
    }
    seen.add(checkType);
    result.push({
      check_type: checkType,
      arguments:
        typeof args === "object" && args !== null && !Array.isArray(args)
          ? (args as Record<string, unknown>)
          : {},
    });
  };

  checks.forEach((check) => {
    pushCheck(check.key, check.check_arguments);
  });

  bundles.forEach((bundle) => {
    if (bundle.checks?.length) {
      bundle.checks.forEach((check) => {
        pushCheck(check.type, check.check_arguments);
      });
      return;
    }

    Object.entries(bundle.check_types_with_arguments || {}).forEach(
      ([checkType, args]) => {
        pushCheck(checkType, args);
      }
    );
  });

  return result;
}

export type PackageChannelFlag = "is_ats_package" | "is_api_package";

export function applyPackageChannelToggle(
  channel: PackageChannelFlag,
  selected: boolean
): Partial<Record<PackageChannelFlag, boolean>> {
  const other: PackageChannelFlag =
    channel === "is_ats_package" ? "is_api_package" : "is_ats_package";
  return { [channel]: selected, ...(selected && { [other]: false }) };
}

export function shouldShowClientInputConfiguration(
  packageData: Package
): boolean {
  return (
    (Boolean(packageData.is_ats_package) ||
      Boolean(packageData.is_api_package)) &&
    packageRequiresClientInputClaims(packageData)
  );
}

export function packageContextToAPI(packageData: Package, user?: UserDetails) {
  const includeClientClaims = shouldShowClientInputConfiguration(packageData);

  return {
    description: packageData.description,
    checks: getCheckTypesWithArguments(packageData.bundles, packageData.checks),
    tag_names: packageData.tag_names.map((tag) => tag.tagValue),
    bundles: packageData.bundles
      .map((bundle) => bundle.id)
      .filter((id, index, self) => self.indexOf(id) === index),
    name: packageData.name,
    rubric_id: packageData.rubric_id,
    is_ats_package: packageData.is_ats_package || false,
    is_api_package: packageData.is_api_package || false,
    is_applicant_pay: packageData.is_applicant_pay || false,
    ...(includeClientClaims
      ? { client_input_claims: packageData.client_input_claims || {} }
      : {}),
  };
}

export function getRequiredChecksFromSelectedChecks(
  selectedChecks: SelectedChecksType[] | []
): string[] {
  if (selectedChecks.length === 0) {
    return [];
  }
  const requiredChecks = selectedChecks.reduce<string[]>((acc, check) => {
    return [...acc, ...(check.required_check_types || [])];
  }, []);
  return requiredChecks;
}

export function getRequiredChecksFromArguments(
  check_arguments: Record<string, unknown>
) {
  const requiredChecks = check_arguments["required_check_types"]
    ? (check_arguments["required_check_types"] as string[])
    : [];
  return requiredChecks;
}

export function getAvailableChildChecks(check: CheckData) {
  return check.children?.filter((child) => {
    const errorType = child.check_has_ordering_error_for_customer_group?.type;
    return errorType !== "error" && errorType !== "warning";
  });
}

export function getAvailableSelectOptions(check: CheckData) {
  return check.select_options?.filter(
    (option) => !option.option_has_ordering_error_for_customer_group
  );
}

/* If the check is an umbrella check, then use the price of its most appropriate child check. */
export function getCheckPrice(
  check: CheckData,
  selectedOption?: OrderingUmbrellaSelectOption
): number {
  return (
    selectedOption?.price ??
    getAvailableSelectOptions(check)?.[0]?.price ??
    getAvailableChildChecks(check)?.[0]?.price ??
    check.price
  );
}

function getSelectedCheckReviewPrice(check: SelectedChecksType): number {
  if (typeof check.cost === "number") {
    return check.cost;
  }

  return 0;
}

export function calculatePackageReviewPrice(
  packageData: Package,
  currency: "USD" | "EUR" | "GBP" | "AUD" | "CAD" | "NZD"
) {
  const checkPrices = packageData.checks.map(getSelectedCheckReviewPrice);
  const bundlePrices = packageData.bundles.map((bundle) => bundle.total);
  const addOnCost = getTotalAddOnsCost(packageData.checks);

  return {
    price: Number(
      (
        [...checkPrices, ...bundlePrices].reduce((a, b) => a + b, 0) + addOnCost
      ).toFixed(2)
    ),
    currency,
  };
}

export function getMinCheckPrice(check: CheckData): number {
  const availableSelectOptionPrices = getAvailableSelectOptions(check)?.flatMap(
    (option) => (option.price ? [option.price] : [])
  );
  if (availableSelectOptionPrices?.length) {
    return Math.min(...availableSelectOptionPrices);
  }
  const availableChildCheckPrices = getAvailableChildChecks(check)?.flatMap(
    (child) => (child.price ? [child.price] : [])
  );
  if (availableChildCheckPrices?.length) {
    return Math.min(...availableChildCheckPrices);
  }
  return check.price;
}

/*
  Get new set of selected checks based on the currently selected checks
  plus all required checks of each selected check as well as the required checks
  of the selected option for that check.
*/
export function getUpdatedChecks(
  check_arguments: Record<string, unknown>,
  availableChecks: CheckData[] | [],
  selectedChecks: SelectedChecksType[],
  check: CheckData,
  selectedOptions?: OrderingUmbrellaSelectOption[]
): SelectedChecksType[] {
  const userSelectedChecks = selectedChecks.map((selectedCheck) => {
    if (selectedCheck.key === check.type) {
      // Merge with existing check_arguments so independently set add-on
      // state (e.g. include_cv_review) is preserved when selection args change.
      const updated_check_arguments = {
        ...selectedCheck.check_arguments,
        ...check_arguments,
      };
      if (
        !("template_id" in check_arguments) ||
        check_arguments.template_id == null
      ) {
        delete updated_check_arguments.template_id;
      }

      if (selectedOptions) {
        const totalCost = selectedOptions.reduce(
          (acc, option) => acc + getCheckPrice(check, option),
          0
        );

        return {
          ...selectedCheck,
          check_arguments: updated_check_arguments,
          cost: totalCost,
          required_check_types: [
            ...(check.required_check_types || []),
            ...(selectedOptions?.flatMap(
              (option) => option.required_check_types ?? []
            ) ?? []),
          ],
          selectedOptions,
          add_on_options: check.add_on_options,
        };
      }
      return {
        ...selectedCheck,
        cost: getCheckPrice(check),
        check_arguments: updated_check_arguments,
        add_on_options: check.add_on_options,
      };
    }
    return selectedCheck;
  });

  const requiredChecks = userSelectedChecks.flatMap(
    (c) => c.required_check_types || []
  );

  const requiredCheckTypesToOrder = availableChecks.filter(
    ({ type }) =>
      requiredChecks.includes(type) &&
      !selectedChecks.find(({ key }) => key === type)
  );

  if (requiredCheckTypesToOrder.length === 0) {
    return userSelectedChecks;
  }
  const requiredCheckTypesKeys = requiredCheckTypesToOrder.map(
    (requiredCheck) => {
      const checkDetails = availableChecks.filter(
        (check) => check.type === requiredCheck.type
      )[0];
      return {
        key: requiredCheck.type,
        name: requiredCheck.name,
        cost: getCheckPrice(requiredCheck),
        currency: requiredCheck.currency,
        check_arguments: {},
        checkDetails,
        must_be_ordered_with_one_of: requiredCheck.must_be_ordered_with_one_of,
      };
    }
  );

  return [...userSelectedChecks, ...requiredCheckTypesKeys];
}

export function getSelectedChecksExclMutuallyExclusiveInclRequired(
  availableChecks: CheckData[],
  selectedChecks: SelectedChecksType[],
  newlySelectedCheck: CheckData,
  requiredCheckTypes: string[] | undefined
) {
  // filter out selected checks which are mutually exclusive to the newly selected check
  const selectedChecksExcludingExclusiveToNewCheck = selectedChecks.filter(
    ({ key }) =>
      !availableChecks
        .find(({ type }) => type === key)!
        .mutually_exclusive_check_types.includes(newlySelectedCheck.type)
  );

  // filter out checks which the newly selected check is mutually exclusive to
  const newCheckMutuallyExclusiveChecks =
    newlySelectedCheck.mutually_exclusive_check_types;

  const exclusiveChecksRemoved =
    selectedChecksExcludingExclusiveToNewCheck.filter(
      ({ key }) => !newCheckMutuallyExclusiveChecks.includes(key)
    );

  const requiredCheckTypesToOrder = availableChecks.filter(
    ({ type }) =>
      requiredCheckTypes?.includes(type) &&
      !selectedChecks.find(({ key }) => key === type)
  );

  const requiredCheckTypesKeys = requiredCheckTypesToOrder.map((check) => ({
    key: check.type,
    name: check.name,
    cost: getCheckPrice(check),
    currency: check.currency,
    check_arguments: {},
    select_options: check.select_options,
    required_check_types: check.required_check_types,
    must_be_ordered_with_one_of: check.must_be_ordered_with_one_of,
    checkDetails: check,
  }));

  return [...exclusiveChecksRemoved, ...requiredCheckTypesKeys];
}

export function getSelectedChecksExclRemovedExclStandalone(
  selectedChecks: SelectedChecksType[],
  removedCheck: CheckData
) {
  const selectedChecksExcludingRemoved = selectedChecks.filter(
    ({ key }) => key !== removedCheck.type
  );

  // filter out standalone checks that are no longer ordered with a required check type
  const excludingStandaloneChecks = selectedChecksExcludingRemoved.filter(
    (selectedCheck) =>
      !isCheckStandaloneDisabled(selectedChecksExcludingRemoved, selectedCheck)
  );

  return excludingStandaloneChecks;
}

export function getItemValuesFromChildren(children: OrderingCheckListChild[]) {
  return children.map((child) => {
    return { value: child.type, label: child.name, price: child.price };
  });
}

export function getAllCheckTypes(packageData: Package): string[] {
  const allCheckTypes: string[] = [];

  (packageData.checks || []).forEach((check) => {
    allCheckTypes.push(check.key);
    if (
      check.check_arguments &&
      typeof check.check_arguments === "object" &&
      "explicit_check_type" in check.check_arguments
    ) {
      const explicitTypes = (
        check.check_arguments as { explicit_check_type?: string[] }
      ).explicit_check_type;
      if (Array.isArray(explicitTypes)) {
        allCheckTypes.push(...explicitTypes);
      }
    }
  });

  (packageData.bundles || []).forEach((bundle) => {
    if (bundle.checks?.length) {
      bundle.checks.forEach((check) => {
        allCheckTypes.push(check.type);
        if (
          check.check_arguments &&
          typeof check.check_arguments === "object" &&
          "explicit_check_type" in check.check_arguments
        ) {
          const explicitTypes = (
            check.check_arguments as { explicit_check_type?: string[] }
          ).explicit_check_type;
          if (Array.isArray(explicitTypes)) {
            allCheckTypes.push(...explicitTypes);
          }
        }
      });
      return;
    }

    Object.entries(bundle.check_types_with_arguments || {}).forEach(
      ([checkType, args]) => {
        allCheckTypes.push(checkType);
        if (args && typeof args === "object" && "explicit_check_type" in args) {
          const explicitTypes = (args as { explicit_check_type?: string[] })
            .explicit_check_type;
          if (Array.isArray(explicitTypes)) {
            allCheckTypes.push(...explicitTypes);
          }
        }
      }
    );
  });

  return allCheckTypes;
}

/**
 * Check type patterns that require client input claims configuration for ATS packages.
 * Add new patterns here when additional check types need client input configuration.
 *
 * Address-based criminal and credit umbrellas (`CRIMINAL_RECORD_REPORT_1` /
 * `CREDIT_REPORT_1` with MULTI_REGION / CURRENT_REGION) are handled separately
 * in `packageRequiresClientInputClaims` — those child types are not known until
 * the applicant submits addresses, so they never appear in `getAllCheckTypes`.
 */
const CLIENT_INPUT_REQUIRED_CHECK_PATTERNS = [
  "NCCHC", // ACIC checks (Australia)
  "UK_DBS", // DBS checks (UK) - covers UK_DBS, BASIC_UK_DBS, STANDARD_UK_DBS, ENHANCED_UK_DBS
  "AU_CRIMINAL_RECORD_2", // AFP checks (Australia)
];

export function hasChecksRequiringClientInputClaims(
  checkTypes: string[]
): boolean {
  return checkTypes.some(
    (checkType) =>
      checkType &&
      CLIENT_INPUT_REQUIRED_CHECK_PATTERNS.some((pattern) =>
        checkType.includes(pattern)
      )
  );
}

export function packageRequiresClientInputClaims(
  packageData: Package
): boolean {
  if (hasChecksRequiringClientInputClaims(getAllCheckTypes(packageData))) {
    return true;
  }

  // packageContextToAPI posts standalone checks only (avoid 409 provenance).
  // Claims still walk bundle composition so address-based criminal/credit
  // umbrellas on a bundle require ATS client-input fields.
  return getCheckTypesWithArgumentsIncludingBundles(
    packageData.bundles || [],
    packageData.checks || []
  ).some(({ check_type, arguments: args }) =>
    isAddressBasedCheck(check_type, args)
  );
}

export const MISSING_CLIENT_INPUT_CLAIMS_MESSAGE =
  "Packages for ATS or API orders with NCCHC, AFP, DBS, or credit checks must have configured input fields.";

export function getMissingClientInputClaimsMessage(t: TFunction): string {
  return t(
    "Packages for ATS or API orders with NCCHC, AFP, DBS, or credit checks must have configured input fields."
  );
}

export function getPackageClientFormPayload(
  packageData: Package
): Record<string, Record<string, unknown>> {
  return Object.fromEntries(
    getCheckTypesWithArgumentsIncludingBundles(
      packageData.bundles || [],
      packageData.checks || []
    ).map(({ check_type, arguments: args }) => [check_type, args])
  );
}
